import { forwardRef } from 'react'

import { clsx } from 'clsx'

import { PostCardContent } from '~publish/components/PostCardContent'
import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import { ContentItemMedia } from '~publish/components/ContentItemCard/ContentItemMedia'
import { isOfType } from '~publish/helpers/typeGuards'

import contentItemCardStyles from '~publish/components/ContentItemCard/ContentItemCard.module.css'
import styles from './BoardCardHoverMenu.module.css'

import { BoardContentItemCardActions } from './BoardContentItemCardActions'

type BoardContentItemCardProps = {
  contentItem: ContentItemDraft
  className?: string
}

/**
 * Board's channel-less-draft card, mirroring `BoardPostCard`: content only,
 * no footer/meta row, a hover-revealed "More" menu instead.
 */
export const BoardContentItemCard = forwardRef<
  HTMLElement,
  BoardContentItemCardProps
>(({ contentItem, className }, ref) => {
  const { body } = contentItem

  // The list only ever passes DraftContent-bodied items; guard so the card is
  // safe to render for any ContentItem without leaking a channel-post body.
  if (!isOfType(body, 'DraftContent')) {
    return null
  }

  return (
    <article
      ref={ref}
      data-testid="board-content-item-card"
      data-content-item-id={contentItem.id}
      className={clsx(contentItemCardStyles.card, styles.container, className)}
    >
      <div className={contentItemCardStyles.inner}>
        <div className={contentItemCardStyles.body}>
          <div className={contentItemCardStyles.content}>
            <PostCardContent text={body.text} annotations={[]} />
            <ContentItemMedia assets={body.assets} />
          </div>
        </div>
      </div>
      <div className={styles.menu}>
        <BoardContentItemCardActions contentItem={contentItem} />
      </div>
    </article>
  )
})

BoardContentItemCard.displayName = 'BoardContentItemCard'
